import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';

export default function Landing() {
  const navigate = useNavigate();
  const [groupCode, setGroupCode] = useState('');
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [isLoadingLogin, setIsLoadingLogin] = useState(false);
  const [hasCodeError, setHasCodeError] = useState(false);
  const [hasLoginError, setHasLoginError] = useState(false);

  const handleGroupCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingCode(true);
    setHasCodeError(false);

    try {
      const { data: group, error } = await supabase
        .from('groups')
        .select('*, delivery_addresses!fk_delivery_addresses_group_id(*)')
        .eq('code', groupCode)
        .single();

      if (error || !group) {
        setHasCodeError(true);
        toast.error('Código de grupo inválido');
        setIsLoadingCode(false);
        return;
      }

      // Verificar el estado del grupo
      let canAccess = false;
      
      // PRIORIDAD 1: Si es Pendiente de Pago (2), acceso TOTAL.
      if (group.status_id === 2) {
        canAccess = true;
      } else {
        // PRIORIDAD 2: Solo si NO es status 2, miramos si hay reposición
        const { data: reposition } = await supabase
          .from('repositions')
          .select('id')
          .eq('group_id', group.id)
          .is('resolved_at', null)
          .maybeSingle();
        
        if (reposition) {
          canAccess = true;
        }
      }

      if (!canAccess) {
        setHasCodeError(true);
        toast.error('El acceso a este grupo no está disponible en este momento');
        setIsLoadingCode(false);
        return;
      }

      // Creamos un token temporal para el código de grupo
      const tempToken = btoa(`group:${groupCode}`);
      localStorage.setItem('authToken', tempToken);

      // Guardamos la información del grupo en localStorage
      localStorage.setItem('userData', JSON.stringify({
        group: group,
        user: null
      }));

      // Guardamos el tipo de usuario
      localStorage.setItem('userType', 'member');

      // Redirigimos según el estado
      // Si el grupo NO está en pendiente de pago, asumimos que es acceso por reposición
      // (ya que la función de auth solo deja pasar en esos dos casos)
      if (group.status_id !== 2) {
         navigate('/reposition');
      } else {
         // Si es status 2 (Pendiente de Pago), flujo normal de compra
         navigate(`/customize?code=${group.code}`);
      }
    } catch (error) {
      setHasCodeError(true);
      toast.error('Error al verificar el código');
    } finally {
      setIsLoadingCode(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingLogin(true);
    setHasLoginError(false);

    try {
      // La función RPC authenticate_group_user ya valida si es status 2 o si tiene reposición
      const { data: authResult, error: authError } = await supabase
        .rpc('authenticate_group_user', {
          p_username: loginData.username,
          p_password: loginData.password
        });

      if (authError) {
        setHasLoginError(true);
        toast.error('Error de conexión o sistema');
        setIsLoadingLogin(false);
        return;
      }

      if (!authResult) {
        setHasLoginError(true);
        toast.error('Usuario o contraseña incorrectos');
        setIsLoadingLogin(false);
        return;
      }

      // Comprobar si devolvió un error de lógica de negocio (acceso denegado por status)
      if (authResult.error === 'ACCESS_DENIED_STATUS') {
        setHasLoginError(true);
        toast.error('El acceso a este grupo no está disponible en este momento');
        setIsLoadingLogin(false);
        return;
      }

      const { user_type, user_data, group_data } = authResult;

      // Guardamos los datos en localStorage
      localStorage.setItem('userType', user_type);
      localStorage.setItem('userData', JSON.stringify({
        user: user_data,
        group: group_data
      }));

      // Guardamos el token
      const token = btoa(`${user_type}:${user_data.id}`);
      localStorage.setItem('authToken', token);

      // Si es un organizador, redirigimos al dashboard
      if (user_type === 'organizer') {
        // Cargar datos completos del grupo
        try {
          const { data: completeGroupData } = await supabase
            .from('groups')
            .select(`
              *,
              delivery_addresses!fk_delivery_addresses_group_id (*),
              design:designs!designs_group_id_fkey(*),
              status:group_statuses (*)
            `)
            .eq('id', group_data.id)
            .single();

          if (completeGroupData) {
            const updatedUserData = {
              user: user_data,
              group: completeGroupData
            };
            localStorage.setItem('userData', JSON.stringify(updatedUserData));
          }
        } catch (err) {
          // Ignorar error
        }
        navigate('/organizer/dashboard');
      } else {
        // Lógica de miembro (pedido)
        // La función de base de datos ya filtró quién puede entrar (Status 2 OR Reposición Active).
        
        // Si el flag de reposición viene activado y NO es status 2, vamos a reposición
        if (authResult.reposition_active && group_data.status_id !== 2) {
           navigate('/reposition');
           return;
        }
        
        // Si no, flujo normal (customize)
        try {
          // Primero, verificamos si el usuario ya tiene un product_id asignado
          if (user_data.product_id) {
            // Cargar datos del producto directamente
            const { data: productData, error: productError } = await supabase
              .from('products')
              .select(`
                id,
                garment_id,
                color_id,
                size_id,
                garment:garments (
                  id,
                  name
                ),
                color:colors (
                  id,
                  name,
                  hex_code
                ),
                size:garment_sizes (
                  id,
                  name
                )
              `)
              .eq('id', user_data.product_id)
              .single();

            if (productData && !productError) {
              // Cargar personalizaciones si existen
              const { data: customizationsData, error: customizationsError } = await supabase
                .from('order_customizations')
                .select(`
                  order_id,
                  option_id,
                  value,
                  option:customization_options (
                    id,
                    name,
                    value_type
                  )
                `)
                .eq('order_id', user_data.id);

              if (!customizationsError) {
                const groupWithExcel = await withExcelDriveLink(group_data);

                // Crear un objeto de usuario completo con el producto y personalizaciones
                const completeUser = {
                  ...user_data,
                  product: productData,
                  customizations: customizationsData || []
                };

                // Keep group_data from authenticate_group_user (includes excel_file_url and final_arts_url)
                const updatedUserData = {
                  user: completeUser,
                  group: groupWithExcel
                };

                localStorage.setItem('userData', JSON.stringify(updatedUserData));
              }
            } else {
              // Cargar los datos completos del pedido como respaldo
              await loadCompleteOrderData(user_data.id, group_data);
            }
          } else {
            // Si no tiene product_id, cargamos los datos completos del pedido
            await loadCompleteOrderData(user_data.id, group_data);
          }
        } catch (err) {
          // Continuamos con los datos que ya tenemos
        }
        // Redirigimos a customize con el código del grupo
        navigate(`/order-details?code=${group_data.code}`);
      }

    } catch (error: any) {
      setHasLoginError(true);
      toast.error('Error al verificar credenciales');
    } finally {
      setIsLoadingLogin(false);
    }
  };

  async function withExcelDriveLink(groupData: any) {
    const groupId = groupData?.id;
    if (!groupId) return groupData;

    const { data: groupRow } = await supabase
      .from('groups')
      .select('excel_drive_link')
      .eq('id', groupId)
      .maybeSingle();

    if (!groupRow) return groupData;

    return {
      ...groupData,
      excel_drive_link: groupRow.excel_drive_link ?? null,
    };
  }

  // Función auxiliar para cargar los datos completos del pedido
  async function loadCompleteOrderData(userId: string, groupDataFallback: any) {
    // Cargar datos completos del pedido con sus relaciones
    const { data: completeOrderData, error: orderError } = await supabase
      .from('group_orders')
      .select(`
        *,
        product:products (
          id,
          garment_id,
          color_id,
          size_id,
          garment:garments (
            id,
            name
          ),
          color:colors (
            id,
            name,
            hex_code
          ),
          size:garment_sizes (
            id,
            name
          )
        ),
        customizations:order_customizations (
          order_id,
          option_id,
          value,
          option:customization_options (
            id,
            name,
            value_type
          )
        ),
        group:groups!group_orders_group_id_fkey (
          excel_drive_link
        )
      `)
      .eq('id', userId)
      .single();

    if (completeOrderData && !orderError) {
      const { group: nestedGroup, ...orderRow } = completeOrderData;
      const group = {
        ...groupDataFallback,
        excel_drive_link:
          nestedGroup?.excel_drive_link ?? groupDataFallback?.excel_drive_link ?? null,
      };

      const updatedUserData = {
        user: orderRow,
        group,
      };

      localStorage.setItem('userData', JSON.stringify(updatedUserData));
    }
  }
 
  return (
    <div className="min-h-screen bg-gray-50" style={{backgroundImage: 'url(/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'}}>
      <Header />

      {/* Hero Section - con margen superior para compensar header fijo */}
      <div className="relative mt-[80px] lg:mt-[115px] pt-6 sm:pt-8 pb-12 sm:pb-20 overflow-hidden" style={{backgroundImage: 'url(/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="absolute inset-0 bg-grid-pattern opacity-5 z-0"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left Column - Forms */}
            <div className="space-y-6 sm:space-y-10 bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200">
              {/* New Order */}
              <div className={`transition-all duration-300 ease-in-out w-full border-2 ${hasCodeError ? 'border-[#D80000]' : 'border-[#F90000]'} rounded-xl p-6 sm:p-8 bg-[#F90000] text-center`}>
                <div className="mb-6">
                  <div className="flex justify-center mb-4">
                    <img 
                      src="/AreaCliente/nuevopedido.jpg" 
                      alt="NUEVO PEDIDO"
                      className="max-w-full h-auto"
                    />
                  </div>
                  {hasCodeError && (
                    <div className="mt-4 text-white font-medium bg-[#D80000] p-3 rounded-lg shadow-inner flex items-start">
                      <span className="text-xl mr-2">⚠️</span>
                      <p>El código introducido no es válido o el grupo no permite acceso en este momento.</p>
                    </div>
                  )}
                </div>
                <form onSubmit={handleGroupCodeSubmit} className="space-y-5">
                  <div>
                    <div className="relative">
                      <input
                        type="text"
                        value={groupCode}
                        onChange={(e) => {
                          setGroupCode(e.target.value);
                          setHasCodeError(false);
                        }}
                        className={`w-full border-2 transition-all duration-200 ${hasCodeError ? 'border-[#D80000] bg-[#FFF5F5]' : groupCode ? 'border-black' : 'border-gray-300 focus:border-[#F90000]'} rounded-lg h-12 px-4 focus:outline-none focus:ring-2 focus:ring-[#F90000] focus:ring-opacity-50`}
                        placeholder="Introduce el código"
                        required
                        disabled={isLoadingCode}
                      />
                      {hasCodeError && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#D80000]">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoadingCode}
                    className="w-full bg-white text-[#F90000] hover:bg-gray-100 border border-[#F90000] h-12 text-lg font-bold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center"
                  >
                    {isLoadingCode ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verificando...
                      </>
                    ) : (
                      <>Enviar &gt;</>
                    )}
                  </button>
                </form>
              </div>

              {/* Check Order */}
              <div className="transition-all duration-300 ease-in-out w-full border-2 border-[#F90000] rounded-xl p-6 sm:p-8 bg-white shadow-md hover:shadow-lg text-center">
                <div className="mb-6">
                  <div className="flex justify-center mb-4">
                    <img 
                      src="/AreaCliente/consultarpedido.jpg" 
                      alt="CONSULTAR PEDIDO" 
                      className="max-w-full h-auto"
                    />
                  </div>
                    
                  {hasLoginError && (
                    <div className="mt-4 text-white font-medium bg-[#F90000] p-3 rounded-lg shadow-inner flex items-start">
                      <span className="text-xl mr-2">⚠️</span>
                      <p>Usuario o contraseña incorrectos. Por favor, verifica tus credenciales.</p>
                    </div>
                  )}
                </div>
                <form onSubmit={handleLoginSubmit} className="space-y-5">
                  <div>
                    <div className="relative">
                      <input
                        type="text"
                        value={loginData.username}
                        onChange={(e) => {
                          setLoginData(prev => ({ ...prev, username: e.target.value }));
                          setHasLoginError(false);
                        }}
                        className={`w-full border-2 transition-all duration-200 ${hasLoginError ? 'border-[#F90000] bg-[#FFF5F5]' : loginData.username ? 'border-black' : 'border-gray-300 focus:border-[#F90000]'} rounded-lg h-12 px-4 focus:outline-none focus:ring-2 focus:ring-[#F90000] focus:ring-opacity-50`}
                        placeholder="Introduce tu usuario"
                        autoComplete="username"
                        required
                        disabled={isLoadingLogin}
                      />
                      {hasLoginError && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#F90000]">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <input
                        type="password"
                        value={loginData.password}
                        onChange={(e) => {
                          setLoginData(prev => ({ ...prev, password: e.target.value }));
                          setHasLoginError(false);
                        }}
                        className={`w-full border-2 transition-all duration-200 ${hasLoginError ? 'border-[#F90000] bg-[#FFF5F5]' : loginData.password ? 'border-black' : 'border-gray-300 focus:border-[#F90000]'} rounded-lg h-12 px-4 focus:outline-none focus:ring-2 focus:ring-[#F90000] focus:ring-opacity-50`}
                        placeholder="Introduce tu contraseña"
                        autoComplete="current-password"
                        required
                        disabled={isLoadingLogin}
                      />
                      {hasLoginError && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#F90000]">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoadingLogin}
                    className="w-full bg-[#F90000] text-white h-12 text-lg font-bold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:bg-[#E00000] disabled:opacity-50 flex items-center justify-center"
                  >
                    {isLoadingLogin ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verificando...
                      </>
                    ) : (
                      <>Enviar &gt;</>
                    )}
                  </button>
                </form>
                {/* Area Roja Image */}
                <div className="flex justify-center mt-4">
                  <img 
                    src="/arearoja.jpg" 
                    alt="Area Roja" 
                    className="h-auto max-w-full"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="flex items-center justify-center">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-300 hover:scale-[1.02]">
                <img 
                  src="/entrada.jpg" 
                  alt="Entrada" 
                  className="w-full h-auto object-cover" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Separador rojo */}
      <div className="w-full bg-[#F90000] h-1"></div>

      {/* Lead Form Section */}
      <div className="py-12 sm:py-20" style={{backgroundImage: 'url(/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-start">
            <div className="lg:order-2">
              <a
                href="https://wa.me/34608510088"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gradient-to-r from-[#F90000] to-[#E00000] text-white p-5 sm:p-7 rounded-2xl mb-6 sm:mb-10 shadow-lg transform transition-transform hover:translate-y-[-5px] no-underline"
              >
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xl sm:text-2xl font-bold">CONTACTO</span>
                    <svg className="w-7 h-7 sm:w-8 sm:h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="text-base sm:text-xl italic font-light">WhatsApp solicita tu presupuesto</div>
                    <div className="text-2xl sm:text-3xl font-extrabold">608 510 088</div>
                  </div>
                </div>
              </a>
            </div>
            <div className="relative h-[400px] sm:h-[850px] overflow-hidden rounded-2xl shadow-xl lg:order-1 transform transition-all duration-300 hover:shadow-2xl">
              <img
                src="/solicitar1.jpg"
                alt="Solicitar"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/20 pointer-events-none"></div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}